import { studySummary } from "@ir/contracts";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Minimize2 } from "lucide-react";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { StudyViewer } from "@/features/viewer/StudyViewer";
import { ViewerHeader } from "@/features/viewer/ViewerHeader";
import { useExpandedViewer } from "@/features/viewer/useExpandedViewer";
import { useIsSmallScreen } from "@/features/viewer/useIsSmallScreen";
import { ApiError, apiFetch } from "@/lib/api";
import { cn } from "@/lib/cn";
import { optionalProp } from "@/lib/optionalProp";

export const Route = createFileRoute("/_auth/insights/$id")({ component: ViewerPage });

function ViewerPage() {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const isSmallScreen = useIsSmallScreen();
  const contenedor = useRef<HTMLDivElement>(null);
  const [ampliado, alternarAmpliado] = useExpandedViewer(contenedor);

  const { data, isPending, error } = useQuery({
    queryKey: ["study", id],
    queryFn: () => apiFetch(`/api/studies/${id}`, { schema: studySummary }),
    retry: false,
  });

  if (isPending) {
    return <p className="p-8 text-nota text-ink3">{t("viewer.loading")}</p>;
  }

  if (error) {
    const message =
      error instanceof ApiError && error.status === 403
        ? t("viewer.forbidden")
        : t("common.error");
    return <p className="p-8 text-nota text-warn">{message}</p>;
  }

  const sePuedeAmpliar = data.hasContent && !isSmallScreen;

  return (
    // Ampliar solo cambia clases de este contenedor: el <iframe> no se mueve
    // del DOM, así que el dashboard no se recarga ni pierde su estado interno.
    // z-30 y z-40 ya los ocupa AppShell (velo y barra lateral del cajón).
    <div
      ref={contenedor}
      className={cn(
        "flex min-h-0 flex-1 flex-col",
        ampliado && "fixed inset-0 z-50 bg-white",
      )}
    >
      {!ampliado && (
        <ViewerHeader
          title={data.title}
          description={data.description}
          {...optionalProp("onExpand", sePuedeAmpliar ? alternarAmpliado : undefined)}
        />
      )}

      {data.hasContent ? (
        <StudyViewer studyId={id} title={data.title} isSmallScreen={isSmallScreen} />
      ) : (
        <p className="p-8 text-nota text-ink3">{t("viewer.missing")}</p>
      )}

      {ampliado && (
        // Siempre visible: el origen opaco del <iframe> impide saber que el
        // ratón se movió sobre él, así que no puede aparecer al acercarse.
        <button
          type="button"
          onClick={alternarAmpliado}
          aria-label={t("viewer.exitExpand")}
          className="absolute top-3 right-3 z-10 rounded-[3px] bg-ink/70 p-1.5 text-white shadow-sm transition-colors hover:bg-ink"
        >
          <Minimize2 size={15} strokeWidth={1.75} aria-hidden />
        </button>
      )}
    </div>
  );
}
